import * as Sentry from '@sentry/react';

import { isE2EEnvironment } from 'utils/is-e2e-environment.util';
import { parseTracesSampleRate } from 'utils/parse-traces-sample-rate.util';
import {
  scrubBreadcrumbUrls,
  scrubRecordingEvent,
  scrubRequestUrl,
  scrubSpan,
} from 'utils/scrub-url.util';
import { captureAndStripUrlCredentials } from 'utils/url-credentials.util';

const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT;
const isLocalDev = environment === 'local';
// E2e runs (dedicated e2e domains or a Cypress-driven browser) must not
// record session replays or traces — they would consume the org-wide replay
// and transaction budgets. Tracing was outside this gate at first, and dev
// then billed 7,744 Cypress pageloads against 24 real ones (86bc02ugj).
const isE2E = isE2EEnvironment() || 'Cypress' in window;

// Before Sentry starts: Replay snapshots location.href when recording
// begins, and its recording-event hook is never called for the rrweb meta
// event that carries it. Emptying the address bar first is what keeps the
// verify-email token out of replay metadata (86bc02ugy).
captureAndStripUrlCredentials();

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,

  // Off: nothing here needs the IP address, and URLs are scrubbed below (86bc02ugy).
  sendDefaultPii: false,
  // Page loads and Web Vitals for real users (86bc02ugj). Low-traffic staff
  // app, so every environment runs at 1.0 unless its build arg says otherwise.
  integrations: isE2E
    ? []
    : [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          beforeAddRecordingEvent: scrubRecordingEvent,
        }),
      ],
  tracesSampleRate: parseTracesSampleRate(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE
  ),
  replaysSessionSampleRate: isLocalDev || isE2E ? 0 : 1.0,
  replaysOnErrorSampleRate: isE2E ? 0 : 1.0,
  environment,
  // /verify-email?token= puts a credential in the URL, so every URL channel is
  // scrubbed: replay's page URL, breadcrumbs, and request.url (86bc02ugy).
  beforeBreadcrumb: scrubBreadcrumbUrls,
  beforeSendTransaction: scrubRequestUrl,
  // Fetch/XHR spans carry request URLs, including GCS signed-URL credentials.
  beforeSendSpan: scrubSpan,
  beforeSend(event) {
    scrubRequestUrl(event);
    // Strip password fields from any captured request bodies to prevent PII leakage.
    // Mirrors the redaction in @verifyiq/sentry-config, which this legacy CRA app
    // cannot import until its Vite migration (react-scripts cannot compile libs/).
    if (event.request?.data && typeof event.request.data === 'object') {
      const data = event.request.data as Record<string, unknown>;
      const sensitiveKeys = [
        'password',
        'confirmPassword',
        'newPassword',
        'currentPassword',
      ];
      const hasAny = sensitiveKeys.some((k) => k in data);
      if (hasAny) {
        const sanitized = { ...data };
        for (const key of sensitiveKeys) {
          if (key in sanitized) sanitized[key] = '[Filtered]';
        }
        return { ...event, request: { ...event.request, data: sanitized } };
      }
    }
    return event;
  },
});
